import { useEffect } from 'react';

const OG_IMAGE = 'https://matchmarble.com/og-image.png';

interface PageMetaOptions {
  title: string;
  description: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
}

export function usePageMeta({ title, description, canonical, ogTitle, ogDescription }: PageMetaOptions) {
  useEffect(() => {
    const prev = {
      title: document.title,
      description: getMeta('name', 'description'),
      canonical: getLink('canonical'),
      ogTitle: getMeta('property', 'og:title'),
      ogDesc: getMeta('property', 'og:description'),
      ogUrl: getMeta('property', 'og:url'),
      ogImage: getMeta('property', 'og:image'),
      twTitle: getMeta('name', 'twitter:title'),
      twDesc: getMeta('name', 'twitter:description'),
    };

    const resolvedTitle = ogTitle || title;
    const resolvedDesc  = ogDescription || description;

    document.title = title;
    setMeta('name',     'description',       description);
    setLink('canonical',                      canonical);
    setMeta('property', 'og:title',          resolvedTitle);
    setMeta('property', 'og:description',    resolvedDesc);
    setMeta('property', 'og:url',            canonical);
    setMeta('property', 'og:image',          OG_IMAGE);
    setMeta('name',     'twitter:title',     resolvedTitle);
    setMeta('name',     'twitter:description', resolvedDesc);

    return () => {
      document.title = prev.title;
      if (prev.description !== null) setMeta('name',     'description',       prev.description);
      if (prev.canonical   !== null) setLink('canonical',                      prev.canonical);
      if (prev.ogTitle     !== null) setMeta('property', 'og:title',          prev.ogTitle);
      if (prev.ogDesc      !== null) setMeta('property', 'og:description',    prev.ogDesc);
      if (prev.ogUrl       !== null) setMeta('property', 'og:url',            prev.ogUrl);
      if (prev.ogImage     !== null) setMeta('property', 'og:image',          prev.ogImage);
      if (prev.twTitle     !== null) setMeta('name',     'twitter:title',     prev.twTitle);
      if (prev.twDesc      !== null) setMeta('name',     'twitter:description', prev.twDesc);
    };
  }, [title, description, canonical, ogTitle, ogDescription]);
}

function getMeta(attr: string, name: string): string | null {
  return document.querySelector(`meta[${attr}="${name}"]`)?.getAttribute('content') ?? null;
}

function getLink(rel: string): string | null {
  return document.querySelector(`link[rel="${rel}"]`)?.getAttribute('href') ?? null;
}

function setMeta(attr: string, name: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}
